import * as betsAPI from "app/api/betsApi";
import * as runnersApi from "app/api/runnersApi";
import Collapsible from "app/components/Collapsible/Collapsible";
import { betPanelHelper } from "app/helpers/betPanelHelper";
import {
  BetBuilderEventsType,
  BetBuilderEventType,
} from "app/types/betBuilderType";
import { UserType } from "app/types/userType";
import { PollingIntervals } from "app/utils/const";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { connect } from "react-redux";
import "../../BetDrawerScreen.scss";
import "./BetBuilder.scss";
import BetBuilderContent from "./components/BetBuilderContent";
import BetBuilderHeader from "./components/BetBuilderHeader";

type Props = {
  betBuilderEvents: BetBuilderEventsType;
  betBuilderEvent: BetBuilderEventType;
  removeBet: (id: number) => void;
  validateBet: (id: number) => Promise<any>;
  updatePricesFromPoll: (eventId: number, runnerIds: number[]) => void;
  autoCollapseBetBuilder: boolean;
  setCollapsibleClosed: (eventId: number) => void;
  setIsCollapsibleOpen: (eventId: number) => void;
  oddsType: string;
  placeBet: Function;
  user: UserType;
};

const BetBuilder: React.FC<Props> = ({
  betBuilderEvents,
  betBuilderEvent,
  removeBet,
  validateBet,
  updatePricesFromPoll,
  setIsCollapsibleOpen,
  setCollapsibleClosed,
  autoCollapseBetBuilder,
  oddsType,
  placeBet,
  user,
}) => {
  const [stake, setStake] = useState<number | null>(null);
  const [potentialReturn, setPotentialReturn] = useState<number>(0);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const toggleCollapsible = () => {
    if (betBuilderEvent.isCollapsibleOpen) {
      setCollapsibleClosed(betBuilderEvent.eventId);
    } else {
      setIsCollapsibleOpen(betBuilderEvent.eventId);
    }
    betPanelHelper.countExpandedCollapsedBetSlips();
  };

  useEffect(() => {
    autoCollapseBetBuilder
      ? setCollapsibleClosed(betBuilderEvent.eventId)
      : setIsCollapsibleOpen(betBuilderEvent.eventId);
  }, [
    autoCollapseBetBuilder,
    betBuilderEvent.eventId,
    setCollapsibleClosed,
    setIsCollapsibleOpen,
  ]);
  const intl = useIntl();

  useEffect(() => {
    const validateStake = () => {
      setErrorMessage("");
      setIsSubmitDisabled(true);

      if (stake === null || stake <= 0) {
        return;
      }

      // Check if api error set
      if (betBuilderEvent.errorMessage?.status !== "LIVE") {
        return;
      }

      // Check if stake exceeds maximum
      if (stake > betBuilderEvent.maxStake) {
        const formattedValue = intl.formatNumber(betBuilderEvent.maxStake, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
          style: "currency",
          currency: user["currencyCode"],
          currencyDisplay: "narrowSymbol",
        });

        setErrorMessage(
          intl.formatMessage(
            { id: "bets-max-stake-error-message" },
            { maxStake: formattedValue }
          )
        );
        return;
      }

      // If all validations pass, enable submit
      setIsSubmitDisabled(false);
    };

    validateStake();
  }, [
    betBuilderEvent.maxStake,
    betBuilderEvent.errorMessage,
    intl,
    stake,
    user,
  ]);

  // Poll for validation (calculatedOdds)
  const pollValidation = async () => {
    const currentLength = betBuilderEvent?.betBuilderItems?.length;
    if (currentLength && currentLength > 1) {
      try {
        await validateBet(betBuilderEvent.eventId);
      } catch (error) {
        console.error("Error occurred during validation:", error);
      }
    }
  };

  useEffect(() => {
    if (betBuilderEvent && betBuilderEvent.betBuilderItems.length > 1) {
      pollValidation();
    }
  }, [betBuilderEvent.calculatedOdds]);

  // Poll for validation (calculatedOdds)
  useEffect(() => {
    // check if betBuilderEvent has exists and has more tha two legs if so  poll validation
    if (betBuilderEvent && betBuilderEvent.betBuilderItems.length > 1) {
      pollValidation();
    }

    // Increase polling frequency on calculate odds call (try 15 seconds and can be adjusted based on feedback)
    const validationInterval = setInterval(pollValidation, 15000);
    return () => clearInterval(validationInterval);
  }, []);

  useEffect(() => {
    const pricesPollingInterval = setInterval(async () => {
      if (!betBuilderEvent.isCollapsibleOpen) return;
      try {
        const runnerIds = betBuilderEvent.betBuilderItems
          .map(item => item.runnerId)
          .filter((id): id is number => id !== undefined);
        if (runnerIds.length > 1) {
          const resp = await runnersApi.getPricesForMultiplesRunners(runnerIds);
          const eventId = betBuilderEvent.eventId;
          updatePricesFromPoll(eventId, resp);
        }
      } catch (error) {
        console.error("Error fetching prices:", error);
      }
    }, PollingIntervals.BETSLIP_PRICE_POLLING_FAST);

    return () => clearInterval(pricesPollingInterval);
  }, [
    betBuilderEvent.betBuilderItems,
    betBuilderEvent.eventId,
    betBuilderEvent.isCollapsibleOpen,
    updatePricesFromPoll,
  ]);

  const handleRemoveBet = () => {
    removeBet(betBuilderEvent.eventId);
  };

  const handleMaxClick = () => {
    setStake(betBuilderEvent.maxStake);
  };

  const submitBet = () => {
    placeBet(
      betBuilderEvent.eventId,
      Number(stake),
      oddsType,
      betBuilderEvent.calculatedOdds
    );
  };

  useEffect(() => {
    if (stake < 1) {
      setPotentialReturn(0);
    }
    const getPotentialProfit = async (stake: number, odd: number) => {
      if (isNaN(odd)) return;
      const oddsType =
        user.preferences?.oddsType || localStorage.getItem("oddsType") || "";

      try {
        const response = await betsAPI.fetchPotentialProfit(
          stake,
          odd,
          oddsType
        );
        setPotentialReturn(response.potentialProfit);
      } catch (error) {
        console.error("Failed to fetch potential profit:", error);
        setPotentialReturn(0);
      }
    };
    if (stake && betBuilderEvent.calculatedOdds) {
      getPotentialProfit(stake, betBuilderEvent.calculatedOdds);
    }
  }, [stake, betBuilderEvent.calculatedOdds, user.preferences?.oddsType]);

  // Call validateBet when oddsType changes
  useEffect(() => {
    if (betBuilderEvent && betBuilderEvent.betBuilderItems.length > 1) {
      validateBet(betBuilderEvent.eventId);
    }
  }, [oddsType]);

  return (
    <div className="bet-slip-container">
      <Collapsible
        header={
          <BetBuilderHeader
            betBuilderEvent={betBuilderEvent}
            toggleCollapsible={toggleCollapsible}
            oddsType={oddsType}
          />
        }
        content={
          <BetBuilderContent
            betBuilderEvent={betBuilderEvent}
            stake={stake}
            setStake={setStake}
            isSubmitDisabled={isSubmitDisabled}
            submitBet={submitBet}
            handleMaxClick={handleMaxClick}
            removeBet={handleRemoveBet}
            errorMessage={errorMessage}
            potentialReturn={potentialReturn}
          />
        }
        isActive={betBuilderEvent.isCollapsibleOpen}
        onToggle={toggleCollapsible}
        withArrows={true}
      />
    </div>
  );
};

const mapStateToProps = state => ({
  betBuilderEvents: state.betBuilderModel?.betBuilderEvents,
  user: state.userModel.user,
  oddsType: state.userModel?.user?.preferences?.oddsType,
  autoCollapseBetBuilder:
    state.userModel.user?.preferences?.autoCollapseBetBuilder || false,
});

const mapDispatchToProps = (dispatch: any) => ({
  validateBet: (eventId: number) => {
    dispatch.betBuilderModel.validate({ eventId });
  },
  updatePricesFromPoll: (eventId: number, runnerIds: number[]) => {
    dispatch.betBuilderModel.updatePricesFromPoll(runnerIds);
  },
  setIsCollapsibleOpen: (eventId: number) => {
    dispatch.betBuilderModel.setCollapsibleOpen(eventId);
  },
  setCollapsibleClosed: (eventId: number) => {
    dispatch.betBuilderModel.setCollapsibleClosed(eventId);
  },
  placeBet: (
    eventId: number,
    stake: number,
    oddsType: string,
    calculatedOdds: number
  ) => {
    dispatch.betBuilderModel.placeBet({
      eventId,
      stake,
      oddsType,
      calculatedOdds,
      isBetBuilder: true,
    });
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(BetBuilder) as ReturnType<typeof connect>;
